import { convertGMT8ToUTC } from '~/utils/dates'

export type EventId =
  | 'match-and-mingle-2025'
  | 'kl-cf-2024'
  | 'c-cf-2024'
  | 'webinar-0'
  | 'webinar-1'
  | 'hk-wo-24'
  | 'td25'
  | 'hkte-2025'
  | 'real-talk-2025'
  | 'kyouth-info-2025'
  | 'rt-bc-2025'

type EventType =
  | 'Career Fair'
  | 'Webinar'
  | 'Panel Discussion'
  | 'Info Session'
  | 'Outreach Programme'
  | 'Networking Mixer'

type EventBanner = {
  light: boolean
  mainImg: string
  contentImg: string
  description: string
  borderTop?: string
  borderBottom?: string
  border?: string
}

type EventDate = {
  from: number
  to: number
}

export type EventLinkMedia = {
  type: 'quote' | 'video' | 'image'
  url?: string
  quote?: {
    text: string
    speaker: string
    color: 'blue' | 'yellow' | 'green'
  }
  video?: {
    url: string
    title: string
  }
}

type EventLink = {
  url: string
  buttonTitle: string
  role?: string
  sectionTitle: string
  description?: string
  points?: string[]
  imageUrl?: string
  mediaScreen?: 'mobile' | 'tablet'
  media?: EventLinkMedia[]
}

export type EventOffer = {
  title: string
  description: string
  imageUrl: string
  actionLink?: string
  buttonTitle?: string
  points?: string[]
}

export type EventScheduleSlot = {
  time: string
  items: {
    title: string
    description?: string
  }[]
}

export type EventSchedule = {
  date: number
  slots: (EventScheduleSlot | string)[]
}

type EventSpeaker = {
  name: string
  imageUrl: string
  job?: string
  logoUrl?: string
  profile?: string
}

type EventTransportationMethod = {
  title: string
  instructions: string[]
  icon: Component
}

export type Event = {
  title: string
  id: EventId
  featured: boolean
  showAtDashboard?: boolean
  type: EventType
  mode: string
  location?: {
    country: string
    full: string
    short: string
    googleMapSrc?: string
  }
  date: EventDate[]
  registrationStatus?: string
  registrationPeriod?: EventDate
  banner: EventBanner
  links: {
    hasMedia: boolean
    links: EventLink[]
  }
  sections: {
    overview: {
      title?: string
      description: string
      image?: string
      showCountdown: boolean
    }
    partnerLinks?: {
      title: string
      subtitle: string
      links: EventLink[]
    }
    statistics?: {
      title: string
      stats: {
        value: string
        description: string
        imageUrl: string
      }[]
    }
    video?: {
      title: string
      link: string
    }
    employers?: {
      title: string
      subtitle: string
      move: boolean
      employers: string[]
    }
    offers?: {
      grid?: 'four' | 'three' | 'two'
      slideImages?: string[]
      offers: EventOffer[]
    }
    attendee?: {
      title: string
      subtitle: string
      attendees: {
        title: string
        imageUrl: string
      }[]
    }
    schedule?: EventSchedule[]
    speakers?: EventSpeaker[]
    locationDetails?: {
      transports?: EventTransportationMethod[]
    }
    roles?: {
      title: string
      subtitle: string
      roles: {
        title: string
        jobs: string[]
        imageUrl: string
        color?: 'purple' | 'blue' | 'green' | 'yellow'
      }[]
    }
    sectors?: {
      title: string
      subtitle: string
      style?: string
      sectors: {
        title: string
        image: string
      }[]
    }
  }
}

export const events: Event[] = [
  {
    id: 'rt-bc-2025',
    title: `Road to #MYBW2025: A Real Talk on Blockchain Career`,
    featured: true,
    showAtDashboard: false,
    type: 'Info Session',
    mode: 'Physical',
    location: {
      country: 'Malaysia',
      short: `Sunway Putra Tower, Kuala Lumpur`,
      full: `Training Room 1 & 2,<br/>Cradle Fund, Level 20,<br/>Sunway Putra Tower,<br/>100, Jalan Putra,<br/>50350 Kuala Lumpur.`,
      googleMapSrc:
        'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3983.727174823262!2d101.69029207543437!3d3.1664003530454194!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31cc483c86c112f1%3A0x9d61b5fcff3523d2!2sCradle%20Fund%20Sdn%20Bhd!5e0!3m2!1sen!2smy!4v1750432833321!5m2!1sen!2smy'
    },
    date: [
      {
        from: convertGMT8ToUTC('2025-07-02T14:30:00'),
        to: convertGMT8ToUTC('2025-07-02T17:30:00')
      }
    ],
    registrationPeriod: {
      from: convertGMT8ToUTC('2025-01-01T00:00:00'),
      to: convertGMT8ToUTC('2025-07-02T14:00:00')
    },
    banner: {
      light: false,
      mainImg: `event-banner-rt-bc-2025.png`,
      contentImg: `event-banner-content-rt-bc-2025.png`,
      description: 'Lorem ipsum'
    },
    links: {
      hasMedia: false,
      links: [
        {
          url: `/signup?purpose=learn&invite_code=rt-bc-2025&next=event-reg&event_id=rt-bc-2025`,
          buttonTitle: `Count Me In!`,
          sectionTitle: `Think Blockchain Is Only for Developers?`,
          description: `Spoiler: <b>It's not.</b> <br/><br/>The world of Web3 needs creatives, strategists, analysts, and more. Find out how your skills can power the future of tech.`
        }
      ]
    },
    sections: {
      overview: {
        title: `Exploring Careers in Blockchain, Crypto, and the Decentralised Future`,
        description: `In partnership with MY Blockchain Week 2025 (#MYBW2025) and Cradle, we're bringing together builders, creators, and aspiring talent to uncover the future of careers in the decentralised world. Whether you're a student exploring your path or a tech enthusiast eager to stay ahead of the curve, this is your chance to hear directly from those building the next generation of the internet.
                <br/><br/>
                Join us on the Road to #MYBW2025: Campus Tour. This is the opportunity for you to meet with students, builders, creators, and industry experts for an insightful night with talks, panels, and networking session! Get ready to dive into the fast-evolving world of blockchain and Web3 technologies!
                <br/><br/>
                <b>About Cradle Fund</b>
                <br/>
                Cradle Fund is Malaysia's early stage start-up influencer, incorporated under the Ministry of Finance Malaysia (MOF) in 2003 with a mandate to fund potential and high-calibre tech start-ups through the Cradle CIP Grant Programme. Cradle also spearheads MYStartup - an initiative comprised of several structured programmes & a startup information portal. The initiative aims to stimulate the creation and development of early-stage startups in Malaysia, which is aligned with Malaysia's goals to produce 5000 startups and 5 unicorns by 2025.
                <br/><br/>
                <b>About MYBW</b>
                <br/>
                MY Blockchain Week (#MYBW) returns in 2025 with a bold theme of “Lift-Off” - signifying a pivotal moment for Malaysia's blockchain ecosystem as it accelerates towards global prominence. This year, MYBW 2025 continues to unite industry leaders, policymakers, and pioneering innovators to drive impactful discussions, foster strategic collaborations, and bridge the gap between emerging opportunities and real-world applications, serving as a launchpad for innovation, education, and regulatory progress. Through this initiative, we reaffirm Malaysia's position as a key block in the global blockchain landscape, empowering individuals, enterprises, and institutions to shape the decentralized future.`,
        showCountdown: false
      },
      offers: {
        grid: 'three',
        offers: [
          {
            title: `Career Stories from Web3 Builders`,
            description: `Hear how leading professionals transitioned into blockchain`,
            imageUrl: `/images/database/events/rt-bc-2025/offer-0.png`
          },
          {
            title: `Global & Local Trends`,
            description: `Gain insights into how Web3 is evolving in Malaysia and beyond`,
            imageUrl: `/images/database/events/rt-bc-2025/offer-1.png`
          },
          {
            title: `Opportunities in the Web3 and Blockchain`,
            description: `Learn about real-world use cases and job roles`,
            imageUrl: `/images/database/events/rt-bc-2025/offer-2.png`
          },
          {
            title: `Student to Startup`,
            description: `Discover how students are getting involved and contributing to the ecosystem`,
            imageUrl: `/images/database/events/rt-bc-2025/offer-3.png`
          },
          {
            title: `Connect & Collaborate`,
            description: `Meet recruiters, startup founders, and tech communities in Web3`,
            imageUrl: `/images/database/events/rt-bc-2025/offer-4.png`
          }
        ]
      },
      schedule: [
        {
          date: convertGMT8ToUTC('2025-07-02T14:00:00'),
          slots: [
            {
              time: `2:30-3:00pm`,
              items: [
                {
                  title: `Registration & Arrival Refreshments`
                }
              ]
            },
            {
              time: `3:00-3:10pm`,
              items: [
                {
                  title: `Opening Remarks`,
                  description: `By TalentLabs & Cradle`
                }
              ]
            },
            {
              time: `3:10-3:20pm`,
              items: [
                {
                  title: `Keynote`,
                  description: `By IOTA`
                }
              ]
            },
            {
              time: `3:20-3:25pm`,
              items: [
                {
                  title: `Short Break for Transition`
                }
              ]
            },
            {
              time: `3:25-4:10pm`,
              items: [
                {
                  title: `Panel Discussion on Web3 Career Opportunities`,
                  description: `<b>Moderator</b> <br/>
                    <ul><li>Adrian Sim, Finance @ MYBW</li></ul>
                    <b>Panelists</b><br/>
                    <ul>
                    <li>Max Lee, Tech & Innovation Lead @ MYBW</li>
                    <li>Nick Khor, Junior Partner @ Growth Charger</li>
                    <li>IOTA Speaker</li>
                    </ul>`
                }
              ]
            },
            {
              time: `4:10-4:25pm`,
              items: [
                {
                  title: `Grant & Programme Sharing`,
                  description: `By TalentLabs & Cradle`
                }
              ]
            },
            {
              time: `4:25-4:35pm`,
              items: [
                {
                  title: `Closing Remarks`,
                  description: `By Noelle Lee & Ian Tan, Chairman & Vice Chairman @ MYBW`
                }
              ]
            },
            {
              time: `4:35-4:45pm`,
              items: [
                {
                  title: `Photo Ops`
                }
              ]
            },
            {
              time: `4:45-5:30pm`,
              items: [
                {
                  title: `Open Networking`
                }
              ]
            }
          ]
        }
      ]
      // employers: {
      //     title: `Meet the <u>Leaders in Blockchain</u>`,
      //     subtitle: ``,
      //     move: false,
      //     employers: []
      // }
    }
  },
  //   {
  //     id: 'real-talk-2025',
  //     title: `Real Talk: Career Journeys in Tech`,
  //     featured: true,
  //     type: `Panel Discussion`,
  //     mode: 'Physical',
  //     location: {
  //       country: 'TBD',
  //       short: 'TBD',
  //       full: 'TBD',
  //       googleMapSrc: `https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3983.6793674515025!2d101.6655699254343!3d3.1788052529686737!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31cc48648f3437bd%3A0x24f3a3018c45b014!2sMalaysia%20International%20Trade%20and%20Exhibition%20Centre!5e0!3m2!1sen!2smy!4v1734903944273!5m2!1sen!2smy`
  //     },
  //     date: [
  //       {
  //         from: convertGMT8ToUTC('2025-06-09T00:00:00'),
  //         to: convertGMT8ToUTC('2025-06-09T23:59:00')
  //       }
  //     ],
  //     registrationPeriod: {
  //       from: convertGMT8ToUTC('2025-01-01T00:00:00'),
  //       to: convertGMT8ToUTC('2025-06-09T23:59:00')
  //     },
  //     banner: {
  //       light: true,
  //       mainImg: 'event-banner-real-talk-2025.png',
  //       contentImg: 'event-banner-content-real-talk-2025.png',
  //       description: 'Lorem ipsum'
  //     },
  //     links: {
  //       hasMedia: false,
  //       links: [
  //         {
  //           url: `/signup?purpose=learn&invite_code=real-talk-2025&next=event-reg&event_id=real-talk-2025&role=jobseeker`,
  //           buttonTitle: `Join now`,
  //           sectionTitle: `Join our event <u>now</u>`,
  //           description: `Lorem ipsum`
  //         }
  //       ]
  //     },
  //     sections: {
  //       overview: {
  //         title: `Not sure where you <u>fit</u> in tech or if you even belong there?`,
  //         description: `You're not alone, and that's exactly why we're hosting this.
  //             <br/><br/>
  //             Real Talk: Career Journeys in Tech is a laid-back, no-fluff panel made for students and fresh grads who want honest answers about what it's really like working in tech. You'll hear from people who've taken the scenic route; career switchers, self-taught coders, and others who didn't follow the “traditional” path.
  //             <br/><br/>
  //             From software and design to data, HR, and more, our speakers will share what helped them land roles, what they wish they knew earlier, and how you can break into tech (even if you're starting from scratch).
  //             <br/><br/>
  //             Stick around after for a chill networking session. Meet people who've been in your shoes and made it work.`,
  //         showCountdown: false
  //       },
  //       offers: {
  //         grid: `four`,
  //         offers: [
  //           {
  //             title: `Hear the Stories Behind Real Tech Careers`,
  //             description: `From career pivots to self-taught journeys—get an honest look at how real people broke into tech.`,
  //             imageUrl: '/images/database/events/m-m-2025/offer-0.jpg'
  //           },
  //           {
  //             title: `Learn from Those Who've Been There`,
  //             description: `Our speakers come from diverse paths and roles. Expect unfiltered insights, not rehearsed success stories.`,
  //             imageUrl: '/images/database/events/m-m-2025/offer-0.jpg'
  //           },
  //           {
  //             title: `Figure Out What's Next (For You)`,
  //             description: `Confused about your next step? Get clarity on what options are out there—and what might actually work for you.`,
  //             imageUrl: '/images/database/events/m-m-2025/offer-0.jpg'
  //           },
  //           {
  //             title: `Connect with People Who Get It`,
  //             description: `Meet others who are figuring it out too, and build real connections with industry folks who've been in your shoes.`,
  //             imageUrl: '/images/database/events/m-m-2025/offer-0.jpg'
  //           }
  //         ]
  //       }
  //     }
  //   },
  {
    id: 'td25',
    title: 'Tech + Digital Career Fair 2025',
    featured: true,
    showAtDashboard: true,
    // registrationStatus: 'Registration opening soon',
    type: 'Career Fair',
    mode: 'Physical',
    location: {
      country: 'Malaysia',
      short: 'MITEC, KL',
      full: 'Kompleks MITEC,<br/>No.8, Jalan Dutamas 2,<br/>50480 Kuala Lumpur, Malaysia',
      googleMapSrc: `https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3983.6793674515025!2d101.6655699254343!3d3.1788052529686737!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31cc48648f3437bd%3A0x24f3a3018c45b014!2sMalaysia%20International%20Trade%20and%20Exhibition%20Centre!5e0!3m2!1sen!2smy!4v1734903944273!5m2!1sen!2smy`
    },
    date: [
      {
        from: convertGMT8ToUTC('2025-08-23T10:00:00'),
        to: convertGMT8ToUTC('2025-08-23T18:00:00')
      },
      {
        from: convertGMT8ToUTC('2025-08-24T10:00:00'),
        to: convertGMT8ToUTC('2025-08-24T18:00:00')
      }
    ],
    registrationPeriod: {
      from: convertGMT8ToUTC('2025-01-01T00:00:00'),
      to: convertGMT8ToUTC('2025-08-23T23:59:00')
    },
    banner: {
      light: false,
      mainImg: 'event-banner-dark.png',
      contentImg: 'event-banner-td25.svg',
      description:
        'The demand for skilled and experienced technology professionals has never been higher. Take the first step into boosting your tech career and get connected with leading companies!'
    },
    links: {
      hasMedia: true,
      links: [
        {
          url: '/signup?purpose=learn&invite_code=td25&next=event-reg&event_id=td25&role=jobseeker',
          buttonTitle: 'Join as Jobseeker',
          sectionTitle: `Take Your Career to the <u>Next Level</u>`,
          description: 'Unlock amazing opportunities and bring your career to new heights:',
          points: [
            '<b>Direct Access:</b><br/> Meet top employers actively looking for talent',
            '<b>Career Insights:</b><br/> Learn about industry trends and in-demand skills',
            '<b>Networking:</b><br/> Build connections with industry professionals and peers',
            '<b>Job Opportunities:</b><br/> Discover roles that match your skills and aspirations'
          ],
          mediaScreen: 'tablet',
          media: [
            {
              type: 'image',
              url: '/images/landing/links-media/event-link-jobseeker-klcf25-0.jpg'
            },
            {
              type: 'image',
              url: '/images/landing/links-media/event-link-jobseeker-klcf25-1.jpg'
            },
            {
              type: 'image',
              url: '/images/landing/links-media/event-link-jobseeker-klcf25-2.jpg'
            }
          ]
        },
        {
          url: '/signup?purpose=recruit&invite_code=td25&next=event-reg&event_id=td25&role=exhibitor',
          buttonTitle: 'Join as Exhibitor',
          sectionTitle: `Find Your Next <u>Game-Changing</u> Talent`,
          description: `T+D25 is the perfect opportunity to expand your team with highly qualified candidates:`,
          points: [
            `<b>Top Talent Access:</b><br/> Connect with highly skilled job seekers from diverse backgrounds`,
            `<b>Employer Branding:</b><br/> Showcase your organization as an industry leader`,
            `<b>Networking:</b><br/> Engage with other exhibitors and industry professionals`,
            `<b>Efficient Hiring:</b><br/> Discover and recruit the perfect candidates for your team`
          ],
          mediaScreen: 'mobile',
          media: [
            {
              type: 'video',
              video: {
                title: `Touch 'n Go Testimony`,
                url: '/images/landing/links-media/event-klcf25-video-exhibitor-tng.mp4'
              }
            },
            {
              type: 'video',
              video: {
                title: 'UMobile Testimony',
                url: '/images/landing/links-media/event-klcf25-video-exhibitor-umobile.mp4'
              }
            },
            {
              type: 'video',
              video: {
                title: 'MBSB Testimony',
                url: '/images/landing/links-media/event-klcf25-video-exhibitor-mbsb.mp4'
              }
            },
            {
              type: 'video',
              video: {
                title: 'Ant International Testimony',
                url: '/images/landing/links-media/event-klcf25-video-exhibitor-ant.mp4'
              }
            },
            {
              type: 'video',
              video: {
                title: 'eCloud Valley Testimony',
                url: '/images/landing/links-media/event-klcf25-video-exhibitor-ecv.mp4'
              }
            },
            {
              type: 'video',
              video: {
                title: 'See You Next Year!',
                url: '/images/landing/links-media/event-klcf25-video-exhibitor-see-u.mp4'
              }
            }
          ]
        }
      ]
    },
    sections: {
      overview: {
        description: `Welcome to the TalentLabs Annual Tech + Digital Career Fair, the premier event where ambition meets opportunity! Building on the overwhelming success of our previous fair, we're back with an even more exceptional experience designed to elevate your career. 
            <br/><br/>
            This year, we are thrilled to bring you another year of unparalleled networking, professional growth, and career advancement. This is your gateway to connecting with leading employers from the tech and digital sectors, all eager to discover top talent like you.
            <br/><br/>
            With an expanded roster of exhibitors, cutting-edge workshops, and invaluable industry insights, you'll have the chance to unlock untapped potential, build meaningful connections, and accelerate your professional growth. Don't miss the chance to seize this pivotal moment in your career journey.`,
        image: '/images/database/events/event-overview-kl-cf-2025.jpg',
        showCountdown: true
      },
      partnerLinks: {
        title: 'Be our awesome <u>partners</u>',
        subtitle: 'Join our exclusive tech career event, and be part of something amazing!',
        links: [
          {
            buttonTitle: 'Join as Media/Community Partner',
            url: '/signup?purpose=recruit&invite_code=td25&next=event-reg&event_id=td25&role=community',
            sectionTitle: 'Be the bridge to future <u>opportunities</u>',
            description:
              'Bring immense value to communities, students, societies, and universities',
            imageUrl: '/images/landing/events/partnerlinks/partnerlink-klcf25-partner.jpg'
            // points: [
            //   `For Students: Industry insights, career readiness, and networking with top employers`,
            //   `For Societies: Boost member engagement, build recognition, and connect with industry leaders`,
            //   `For Universities: Enhance graduate outcomes, align academics with industry trends, and forge employer partnerships`
            // ]
          },
          {
            buttonTitle: 'Join our Startup Zone',
            url: '/signup?purpose=recruit&invite_code=td25&next=event-reg&event_id=td25&role=startup',
            sectionTitle: `Accelerate your <u>startup's journey</u>`,
            description: 'Be part of our growing recruiting and branding event',
            imageUrl: '/images/landing/events/partnerlinks/partnerlink-klcf25-startup.jpg'
            // points: [
            //   `Talent Discovery: Connect with passionate and skilled job seekers ready to join your journey`,
            //   `Brand Visibility: Showcase your startup and attract attention in the tech and digital ecosystem`,
            //   `Networking: Build connections with industry leaders, investors, and fellow entrepreneurs`,
            //   `Cost-Effective Hiring: Find the right talent to fuel your growth without breaking the bank`
            // ]
          }
        ]
      },
      statistics: {
        title: 'Expected 2025 <u>turnout</u>',
        stats: [
          {
            value: '10,000+',
            description: 'Participants',
            imageUrl: '/images/landing/events/statistics/event-stats-person.svg'
          },
          {
            value: '70+',
            description: 'Exhibitors',
            imageUrl: '/images/landing/events/statistics/event-stats-company.svg'
          },
          {
            value: '3000+',
            description: 'Jobs Available',
            imageUrl: '/images/landing/events/statistics/event-stats-briefcase.svg'
          }
        ]
      },
      video: {
        title: `Check out <u>last year's</u> event`,
        link: 'https://www.youtube.com/embed/OR0H_ofZtAs'
      },
      schedule: [
        {
          date: convertGMT8ToUTC('2025-08-23T10:00:00'),
          slots: [
            {
              time: '10am - 6pm',
              items: [
                {
                  title: 'Registration'
                },
                {
                  title: 'Exhibitor Booths'
                },
                {
                  title: 'Startup Zone'
                },
                {
                  title: 'Career Advice Lounge'
                },
                {
                  title: 'Professional Headshot Booth'
                },
                {
                  title: 'Exhibitor Spotlight Sessions'
                },
                {
                  title: 'Private Interview Booths'
                }
              ]
            },
            {
              time: '10:30am',
              items: [
                {
                  title: 'Opening speech'
                }
              ]
            },
            {
              time: '3:00pm',
              items: [
                {
                  title: 'Lucky draw'
                }
              ]
            },
            {
              time: '5:00pm',
              items: [
                {
                  title: 'Lucky draw'
                }
              ]
            },
            {
              time: '5:30pm',
              items: [
                {
                  title: 'Closing speech'
                }
              ]
            }
          ]
        },
        {
          date: convertGMT8ToUTC('2025-08-24T10:00:00'),
          slots: [
            {
              time: '10am - 6pm',
              items: [
                {
                  title: 'Registration'
                },
                {
                  title: 'Exhibitor Booths'
                },
                {
                  title: 'Startup Zone'
                },
                {
                  title: 'Career Advice Lounge'
                },
                {
                  title: 'Professional Headshot Booth'
                },
                {
                  title: 'Exhibitor Spotlight Sessions'
                },
                {
                  title: 'Private Interview Booths'
                }
              ]
            },
            {
              time: '10:30am',
              items: [
                {
                  title: 'Opening speech'
                }
              ]
            },
            {
              time: '3:00pm',
              items: [
                {
                  title: 'Lucky draw'
                }
              ]
            },
            {
              time: '5:00pm',
              items: [
                {
                  title: 'Lucky draw'
                }
              ]
            },
            {
              time: '5:30pm',
              items: [
                {
                  title: 'Closing speech'
                }
              ]
            }
          ]
        }
      ],
      //   locationDetails: {
      //     transports: [
      //       {
      //         title: 'By car',
      //         icon: defineAsyncComponent(() => import('~/components/common/icon/events/IconCar.vue')),
      //         instructions: [
      //           `If you're coming from the city center, take Jalan Tun Razak and head towards Jalan Duta.`,
      //           `Continue straight, and you'll find MITEC on your left-hand side, just before the exit for Jalan Sentul.`,
      //           `There are plenty of parking options available, including valet and self-parking.`
      //         ]
      //       },
      //       {
      //         title: 'By train',
      //         icon: defineAsyncComponent(
      //           () => import('~/components/common/icon/events/IconTrain.vue')
      //         ),
      //         instructions: [
      //           `The nearest MRT station is the Titiwangsa Station, located just a 5-minute walk from MITEC.`,
      //           `Alternatively, you can stop at KL Sentral Station and take a short LRT ride to Titiwangsa for a direct connection.`,
      //           `Both options offer easy access to various parts of the city.`
      //         ]
      //       },
      //       {
      //         title: 'By bus',
      //         icon: defineAsyncComponent(() => import('~/components/common/icon/events/IconBus.vue')),
      //         instructions: [
      //           `The nearest bus stops include MITEC (KL580) and Jalan Sentul (KL603).`,
      //           `Depending on your starting point, you can catch a bus to these stops and enjoy a brief walk to MITEC.`
      //         ]
      //       }
      //     ]
      //   },
      attendee: {
        title: 'The <u>must attend</u> event for',
        subtitle: `Be part of Malaysia's #1 tech career fair`,
        attendees: [
          {
            title: 'Tech Decision Maker',
            imageUrl: '/images/landing/events/attendee/attendee-decision.svg'
          },
          {
            title: 'Product Developers',
            imageUrl: '/images/landing/events/attendee/attendee-product.svg'
          },
          {
            title: 'Business Owners',
            imageUrl: '/images/landing/events/attendee/attendee-business.svg'
          },
          {
            title: 'UI/UX Designers',
            imageUrl: '/images/landing/events/attendee/attendee-uiux.svg'
          },
          {
            title: 'Software Developers',
            imageUrl: '/images/landing/events/attendee/attendee-software.svg'
          },
          {
            title: 'Human Resources',
            imageUrl: '/images/landing/events/attendee/attendee-hr.svg'
          },
          {
            title: 'Data Analysts',
            imageUrl: '/images/landing/events/attendee/attendee-data.svg'
          },
          {
            title: 'Content Strategists',
            imageUrl: '/images/landing/events/attendee/attendee-content.svg'
          },
          {
            title: 'Operations Specialists',
            imageUrl: '/images/landing/events/attendee/attendee-operation.svg'
          },
          {
            title: 'Mobile Developer',
            imageUrl: '/images/landing/events/attendee/attendee-mobile.svg'
          },
          {
            title: 'E-Commerce Specialists',
            imageUrl: '/images/landing/events/attendee/attendee-ecommerce.svg'
          },
          {
            title: 'AI Solutions Architects',
            imageUrl: '/images/landing/events/attendee/attendee-ai.svg'
          },
          {
            title: 'Machine Learning Engineers',
            imageUrl: '/images/landing/events/attendee/attendee-ml.svg'
          },
          {
            title: 'Cloud Engineers',
            imageUrl: '/images/landing/events/attendee/attendee-cloud.svg'
          },
          {
            title: 'Assembly Technicians',
            imageUrl: '/images/landing/events/attendee/attendee-assembly.svg'
          },
          {
            title: 'Quality Assurance Engineer',
            imageUrl: '/images/landing/events/attendee/attendee-qa.svg'
          },
          {
            title: 'Cybersecurity Analysts',
            imageUrl: '/images/landing/events/attendee/attendee-cybersecurity.svg'
          },
          {
            title: 'Telecommunications Architect',
            imageUrl: '/images/landing/events/attendee/attendee-telco.svg'
          },
          {
            title: 'Risk Management Specialists',
            imageUrl: '/images/landing/events/attendee/attendee-risk.svg'
          }
        ]
      },
      roles: {
        title: 'Find roles that <u>fit you</u>',
        subtitle: `No more settling—explore roles that challenge and excite you at this year's fair.`,
        roles: [
          {
            title: 'Software Engineering',
            imageUrl: '/images/landing/events/roles/role-software.png',
            jobs: [
              `Front-End Developer`,
              `Back-End Developer`,
              `Mobile Developer`,
              `DevOps Engineer`,
              `Cloud Architect`,
              `Full Stack Developer`
            ],
            color: 'blue'
          },
          {
            title: 'Creative and Media',
            imageUrl: '/images/landing/events/roles/role-creative.png',
            jobs: [
              `UI/UX Designer`,
              `Content Strategist`,
              `SEO Specialist`,
              `Social Media Executive`,
              `Digital Marketer`,
              `Ad Operations Specialist`
            ],
            color: 'purple'
          },
          {
            title: `Business and Commercial`,
            imageUrl: '/images/landing/events/roles/role-business-woman.png',
            jobs: [
              `Data Analyst`,
              `Technology Consultant`,
              `CRM Specialist`,
              `Product Manager`,
              `E-Commerce Specialist`,
              `AI Solutions Architect`
            ],
            color: 'green'
          },
          {
            title: `Semiconductor & Hardware`,
            imageUrl: '/images/landing/events/roles/role-engineer.png',
            jobs: [
              `Technical Hardware Engineer`,
              `Quality Assurance Engineer`,
              `R&D Data Analyst`,
              `AI Chip Engineer`,
              `Assembly Technician`,
              `Machine Learning Engineer`
            ],
            color: 'yellow'
          },
          {
            title: `Global Business Services`,
            imageUrl: '/images/landing/events/roles/role-business-man.png',
            jobs: [
              `BI Analyst`,
              `RPA Developer`,
              `Cloud Engineer`,
              `Application Support Analyst`,
              `Compliance Analyst`,
              `Risk Management Specialist`
            ],
            color: 'green'
          },
          {
            title: `Telecoms`,
            imageUrl: '/images/landing/events/roles/role-phone.png',
            jobs: [
              `Telecommunications Architect`,
              `Network Engineer`,
              `Systems Engineer`,
              `Field Technician`,
              `Cybersecurity Analyst`,
              `VoIP Technician`
            ],
            color: 'blue'
          }
        ]
      },
      offers: {
        slideImages: [
          '/images/landing/events/offers/image-slide-klcf25-0.png',
          '/images/landing/events/offers/image-slide-klcf25-1.png',
          '/images/landing/events/offers/image-slide-klcf25-2.png',
          '/images/landing/events/offers/image-slide-klcf25-3.png',
          '/images/landing/events/offers/image-slide-klcf25-4.png'
        ],
        offers: [
          {
            title: 'Exhibitor Booths',
            imageUrl: '/images/landing/events/offers/offer-klcf25-0.svg',
            description: 'Discover career opportunities at established, global organizations:',
            points: [
              `Structured career paths details`,
              `Benefits & training programs`,
              `Company culture & career growth`
            ]
          },
          {
            title: `Career Lounge`,
            imageUrl: '/images/landing/events/offers/offer-klcf25-1.svg',
            description: `Get expert advice to help you take the next step in your career:`,
            points: [`Resume optimization`, `Master your elevator pitch`, `Insider interview tips`]
          },
          {
            title: `Professional Headshot`,
            imageUrl: '/images/landing/events/offers/offer-klcf25-2.svg',
            description: `Make a memorable first impression with a professional photo:`,
            points: [
              `High-quality, on-site & free!`,
              `Skilled headshot photographer`,
              `Have your photo on the spot`
            ]
          },
          {
            title: `Exhibitor Spotlight Sessions`,
            imageUrl: '/images/landing/events/offers/offer-klcf25-3.svg',
            description: `Dive into valuable industry insights and opportunities:`,
            points: [
              `Engage with leading experts`,
              `Opportunity to ask questions`,
              `Follow our IG for updates!`
            ]
          },
          {
            title: `Startup Zone (NEW!)`,
            imageUrl: '/images/landing/events/offers/offer-klcf25-4.svg',
            description: `Immerse yourself in a culture of innovation and creativity:`,
            points: [
              `Network directly with startups`,
              `Insights into startup environments`,
              `Directly impact project success`
            ]
          },
          //   {
          //     title: `Private Interview Booths`,
          //     imageUrl: '/images/landing/events/offers/offer-klcf25-5.svg',
          //     description: `Confidential, one-on-one discussions with recruiters:`,
          //     points: [
          //       `Discuss your qualifications and fit for specific roles`,
          //       `Ask detailed questions about the company and position`,
          //       `Receive immediate feedback on your application status`
          //     ]
          //   },
          {
            title: `Lucky Draw + Exclusive Prizes`,
            imageUrl: '/images/landing/events/offers/offer-klcf25-6.svg',
            description: `Add some excitement to your experience and win big:`,
            points: [
              `Exciting prizes up for grabs`,
              `Boost your chances by engaging`,
              `Twice a day, at 3PM and 5PM`
            ]
          }
        ]
      }
    }
  },
  {
    id: 'kyouth-info-2025',
    title: `TalentLabs K-Youth Development Programme 2025 Info-Session`,
    featured: false,
    type: `Info Session`,
    mode: 'Online',
    date: [
      {
        from: convertGMT8ToUTC('2025-06-16T19:30:00'),
        to: convertGMT8ToUTC('2025-06-16T20:30:00')
      }
    ],
    registrationPeriod: {
      from: convertGMT8ToUTC('2025-01-01T00:00:00'),
      to: convertGMT8ToUTC('2025-06-16T19:00:00')
    },
    banner: {
      light: true,
      mainImg: 'event-banner-kyouth-info-2025.png',
      contentImg: 'event-banner-content-kyouth-info-2025.png',
      description: 'Lorem ipsum'
    },
    links: {
      hasMedia: false,
      links: [
        {
          url: `/signup?purpose=learn&invite_code=kyouth-info-2025&next=event-reg&event_id=kyouth-info-2025&role=jobseeker`,
          buttonTitle: 'Register Now',
          sectionTitle: `Kickstart Your Tech Career!`,
          description: `Join our exclusive K-Youth Webinar to learn how you can gain real work experience, earn a monthly allowance, and launch your journey in tech. Spots are limited - don't miss out!`
        }
      ]
    },
    sections: {
      overview: {
        title: `Curious about breaking into tech but not sure where to start?`,
        description: `The K-Youth Development Programme 2025 Info Session is your behind-the-scenes look into one of Malaysia's most impactful upskilling initiatives for fresh grads and jobseekers, powered by Khazanah Nasional.
            <br/><br/>
            Get the real scoop from TalentLabs and K-Youth alumni as we walk you through the programme, from certifications by Google Cloud and ACCA, to hands-on training and a monthly RM2,500 allowance.
            <br/><br/>
            We'll keep it short, helpful, and honest with a live Q&A to get your questions answered directly.
            <br/><br/>
            Whether you're exploring new paths or ready for your next step, this is where your tech journey could begin.`,
        showCountdown: false
      },
      offers: {
        grid: 'three',
        offers: [
          {
            title: `Honest Stories, Real Advice`,
            description: `Hear from people who've navigated career pivots, taught themselves new skills, and found their place in tech, even if they didn't start there.`,
            imageUrl: '/images/database/events/kysi-2025/offer-0.png'
          },
          {
            title: `Unfiltered Alumni Insights`,
            description: `Past K-Youth participants will share what it's really like to switch paths, break into new industries, and go from unsure to employed.`,
            imageUrl: '/images/database/events/kysi-2025/offer-1.png'
          },
          {
            title: `Straight Answers to Big Questions`,
            description: `What roles actually suit your background? How do you stand out without experience? What should you really focus on? Ask it all during our open Q&A session.`,
            imageUrl: '/images/database/events/kysi-2025/offer-2.png'
          },
          {
            title: `Clarity on What's Next`,
            description: `Explore different roles in tech, from engineering and design to HR and analytics, and figure out which path might make the most sense for you.`,
            imageUrl: '/images/database/events/kysi-2025/offer-3.png'
          },
          {
            title: `Connection, Not Just Networking`,
            description: `Meet others who are figuring things out too, plus industry pros, alumni, and the TalentLabs team who are here to help.`,
            imageUrl: '/images/database/events/kysi-2025/offer-4.png'
          },
          {
            title: `Support from TalentLabs`,
            description: `Curious about our programmes? Talk directly with us and see how we can support your next career move, whether you're just starting or pivoting.`,
            imageUrl: '/images/database/events/kysi-2025/offer-5.png'
          }
        ]
      },
      schedule: [
        {
          date: convertGMT8ToUTC('2025-06-16T19:30:00'),
          slots: [
            {
              time: '7:30 - 7:40pm',
              items: [
                {
                  title: 'Participant Log-In & Opening Remarks'
                }
              ]
            },
            {
              time: '7:40 - 7:50pm',
              items: [
                {
                  title: 'K-Youth Development Programme 2025 Sharing Session'
                }
              ]
            },
            {
              time: '7:50 - 8:10pm',
              items: [
                {
                  title: 'Panel Discussion with TalentLabs K-Youth Development Programme Alumni'
                }
              ]
            },
            {
              time: '8:10 - 8:25pm',
              items: [
                {
                  title: 'Q&A Session'
                }
              ]
            },
            {
              time: '8:25 - 8:30pm',
              items: [
                {
                  title: 'Closing Remarks'
                }
              ]
            }
          ]
        }
      ]
    }
  },
  {
    id: 'match-and-mingle-2025',
    title: 'Match and Mingle',
    featured: false,
    type: 'Networking Mixer',
    mode: 'Physical',
    location: {
      country: 'Malaysia',
      short: 'Nale, Mid Valley, KL',
      full: `NALE - The Nasi Lemak Company,<br/>G(E)-013, <br/>Mid Valley Megamall, <br/>Lingkaran Syed Putra, <br/>Mid Valley City,<br/>59200 Kuala Lumpur, Malaysia`,
      googleMapSrc: `https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3983.909988261775!2d101.67709649999999!3d3.1185099!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31cc4990cd0d4473%3A0x99e01b022309ed42!2sNALE%20-%20The%20Nasi%20Lemak%20Company%2C%20Midvalley!5e0!3m2!1sen!2smy!4v1746171902208!5m2!1sen!2smy`
    },
    date: [
      {
        from: convertGMT8ToUTC('2025-05-15T18:30:00'),
        to: convertGMT8ToUTC('2025-05-15T22:00:00')
      }
    ],
    registrationPeriod: {
      from: convertGMT8ToUTC('2025-05-01T00:00:00'),
      to: convertGMT8ToUTC('2025-05-15T22:00:00')
    },
    banner: {
      light: true,
      mainImg: 'event-banner-match-and-mingle-2025.jpg',
      contentImg: 'event-banner-content-match-and-mingle-2025.svg',
      description: `Thinking about taking your career global? What if you didn't have to go far to do it?`
    },
    links: {
      hasMedia: true,
      links: [
        {
          url: '/signup?purpose=learn&invite_code=match-mingle-2025&next=event-reg&event_id=match-and-mingle-2025',
          buttonTitle: 'Join us now',
          sectionTitle: `Real People. <br/>Real Conversations. <br/>Real Possibilities.`,
          description: `Let's unlock partnerships one match at a time.<br/><br/><b>Warning:</b> Side effects may include new clients, partners, or lightbulb moments. <br/>RSVP responsibly.`,
          media: [
            {
              type: 'image',
              url: '/images/database/events/m-m-2025/link.jpg'
            }
          ]
        }
      ]
    },
    sections: {
      overview: {
        title: 'No pressure. No speeches. Just <u>opportunities.</u>',
        description: `Looking to build meaningful connections — without the stiff name tags or awkward panels?
        <br/><br/>
        Match & Mingle is a casual networking night created for HR leaders, talent acquisition professionals, and industry partners who want to connect, exchange ideas, and spark future collaborations in a more relaxed setting.
        <br/><br/>
        We're swapping the formalities for great food, friendly vibes, and a playful twist. Upon arrival, you'll receive a Match Card with half of a quirky pair (think: 🍔 & 🍟, 🛜 & 💻, ☕ & 🍩). Your mission? Find your “other half” and kick off a conversation. It could lead to a future project, a valuable contact, or simply a refreshing chat.
        <br/><br/>
        No speeches. No pressure. Just real conversations, new perspectives, and space to explore meaningful partnerships — one match at a time.`,
        image: '/images/database/events/m-m-2025/overview.jpg',
        showCountdown: false
      },
      schedule: [
        {
          date: convertGMT8ToUTC('2025-05-15T00:00:00'),
          slots: [
            {
              time: '6:30 - 7:00pm',
              items: [
                {
                  title: 'Sign In & Say Hello',
                  description: `Grab your Match Card, get settled, and start scoping out your potential pairings.`
                }
              ]
            },
            {
              time: '7:00 - 7:10pm',
              items: [
                {
                  title: `A Quick Welcome from the TalentLabs Fam`,
                  description: `Just a few words before the mingling magic begins.`
                }
              ]
            },
            {
              time: '7:10 - 10:00pm',
              items: [
                {
                  title: `Mix, Match & Mingle`,
                  description: `Enjoy dinner, break the ice with fun pair-finding games, and spark real conversations`
                }
              ]
            },
            {
              time: '10:00pm',
              items: [
                {
                  title: `That's a Wrap!`,
                  description: `A little love note from us to end the night.`
                }
              ]
            }
          ]
        }
      ],
      offers: {
        grid: 'three',
        offers: [
          {
            title: `Meet professionals`,
            description:
              'A warm, low-pressure setting to connect with professionals across industries',
            imageUrl: '/images/database/events/m-m-2025/offer-0.jpg'
          },
          {
            title: 'Fun icebreakers',
            description: `Lighthearted icebreakers like “Find Your Pair” to make networking fun`,
            imageUrl: '/images/database/events/m-m-2025/offer-1.jpg'
          },
          {
            title: 'Welcoming community',
            description: `A community-first atmosphere where collaboration comes naturally`,
            imageUrl: '/images/database/events/m-m-2025/offer-2.jpg'
          },
          {
            title: `Collaborations`,
            description:
              'Conversations that spark ideas, potential partnerships, or future collabs',
            imageUrl: '/images/database/events/m-m-2025/offer-3.jpg'
          },
          {
            title: `Moments to remember`,
            description: 'Delicious food, relaxed energy, and a few surprise moments along the way',
            imageUrl: '/images/database/events/m-m-2025/offer-4.jpg'
          },
          {
            title: `Networking opportunities`,
            description:
              'Quality networking within the TalentLabs ecosystem of professionals and partners',
            imageUrl: '/images/database/events/m-m-2025/offer-5.jpg'
          }
        ]
      }
    }
  },
  {
    id: 'hkte-2025',
    title: 'Guangdong-Hong Kong-Macao Greater Bay Area Talent Development Showcase',
    featured: false,
    type: 'Outreach Programme',
    mode: 'Physical',
    location: {
      country: 'Malaysia',
      short: 'JW Marriott, KL',
      full: `JW Marriott,<br/>183, Jln Bukit Bintang,<br/>Bukit Bintang,<br/>55100 Kuala Lumpur,<br/>Wilayah Persekutuan Kuala Lumpur,<br/>Malaysia`,
      googleMapSrc: `https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3983.7985674975557!2d101.7138854!3d3.1477847!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31cc362c6f063617%3A0xb61eef789115b4cb!2sJW%20Marriott%20Hotel%20Kuala%20Lumpur!5e0!3m2!1sen!2smy!4v1740516686075!5m2!1sen!2smy`
    },
    date: [
      {
        from: convertGMT8ToUTC('2025-04-12T10:00:00'),
        to: convertGMT8ToUTC('2025-04-12T18:00:00')
      },
      {
        from: convertGMT8ToUTC('2025-04-13T10:00:00'),
        to: convertGMT8ToUTC('2025-04-13T16:00:00')
      }
    ],
    registrationPeriod: {
      from: convertGMT8ToUTC('2025-01-01T00:00:00'),
      to: convertGMT8ToUTC('2025-04-13T23:00:00')
    },
    banner: {
      light: false,
      mainImg: 'event-banner-hkte-2025.png',
      contentImg: 'event-banner-content-hkte-2025.svg',
      description: `Thinking about taking your career global? What if you didn't have to go far to do it?`
    },
    links: {
      hasMedia: true,
      links: [
        {
          url: '/signup?purpose=learn&invite_code=hkte-2025&next=event-reg&event_id=hkte-2025',
          buttonTitle: 'Join us now',
          sectionTitle: `The world is calling! 🇭🇰`,
          description:
            'Join our event and discover new opportunities in the Guangdong-Hong Kong-Macao Greater Bay Area - a place where opportunity and growth meets.',
          media: [
            {
              type: 'image',
              url: '/images/database/events/hkte-2025/link-0.png'
            }
          ]
        }
      ]
    },
    sections: {
      overview: {
        title: 'A World of Opportunities Awaits - <br/><u>Right Next Door</u>',
        description: `<b>Thinking about taking your career global? What if you didn't have to go far to do it?</b>
        <br/><br/>
        The event will include two main components: a Symposium and a Career Fair. The Symposium will feature <a href="#event-schedule">experts</a> from the Greater Bay Area (GBA) discussing the latest developments and opportunities in the region. At the same time, the Career Fair will connect participants with nearly 40 leading GBA enterprises from various sectors, including technology, I&T, engineering, finance, and accounting. <a href='/jobs?campaign=RV12Ik8YR72goDRAn8RWRQ' target='_blank'>Check out the job listings!</a>
        <br/><br/>
        Whether you're a fresh grad exploring possibilities, a seasoned professional seeking for a career boost, or an industry leader seeking cross-border collaborations, this event connects you with top companies, industry experts, and fellow Malaysians who have successfully built their careers in Hong Kong and the GBA.`,
        image: '/images/database/events/hkte-2025/overview.png',
        showCountdown: false
      },
      employers: {
        title: `Meet Hong Kong's <u>top employers</u>`,
        subtitle: '',
        move: true,
        employers: [
          'hkte',
          'tl',
          'tja',
          'china-state-construction',
          'hrssdog',
          'prism',
          'pccw-hkt',
          'hkicpa',
          'rept-battero',
          'grant-thorton-hk',
          'hkihrm',
          'bps-global',
          'atal',
          'sinoland',
          'iet-hk',
          'manulife',
          'hkgcc',
          'macau-comp',
          'gdh',
          'jobsdb-seek',
          'clp',
          'bank-of-china',
          'hsbc',
          'efohk',
          'hkcnsa',
          'tlsohk'
        ]
      },
      //   offers: {
      //     offers: [
      //       {
      //         title: `Break into One of Asia's Fastest-Growing Job Markets`,
      //         imageUrl: '/images/database/events/hkte-2025/offer-0.png',
      //         description: `Hong Kong & the Greater Bay Area are actively seeking global talent—discover the high-demand industries, in-demand skills, and the opportunities that await in this thriving region.`
      //       },
      //       {
      //         title: 'Learn from Malaysians Who Made the Leap',
      //         imageUrl: '/images/database/events/hkte-2025/offer-1.png',
      //         description: `Meet fellow Malaysians who have built successful careers in top industries. Get firsthand insights into their journey and what it's really like to work in Hong Kong and the GBA.`
      //       },
      //       {
      //         title: `Find Out if It's the Right Move for You`,
      //         imageUrl: '/images/database/events/hkte-2025/offer-2.png',
      //         description: `Gain practical insights on salaries, work culture, relocation perks, and talent policies designed to support global professionals like you.`
      //       },
      //       {
      //         title: `Make Career Moves That Open Global Doors`,
      //         imageUrl: '/images/database/events/hkte-2025/offer-3.png',
      //         description: `Whether you're seeking to expand your network, land an overseas role, or future-proofing your career, this event connects you to the right people and opportunities to take your career further.`
      //       }
      //     ]
      //   },
      schedule: [
        {
          date: convertGMT8ToUTC('2025-04-12T00:00:00'),
          slots: [
            'SYMPOSIUM<br/>Mayang Sari Ballroom (Floor B3)',
            {
              time: '10:00 - 10:30 am',
              items: [
                {
                  title: 'Registration'
                }
              ]
            },
            {
              time: '10:30 - 10:45am',
              items: [
                {
                  title: 'Welcome Remarks',
                  description: `By the <b>Hon. Chris Sun Yuk-han, JP</b><br/>
                  Secretary for Labour and Welfare of the Government of the Hong Kong Special Administrative Region`
                }
              ]
            },
            {
              time: '10:45 - 11:00am',
              items: [
                {
                  title: 'Group Photo'
                }
              ]
            },
            {
              time: '11:00 - 11:25pm',
              items: [
                {
                  title: 'Keynote Speech',
                  description:
                    'Introduction of GBA by <b>Mr Daniel Chan, Executive Vice President and Head of GBA, HSBC</b>'
                }
              ]
            },
            {
              time: '11:25 - 11:45pm',
              items: [
                {
                  title: 'A World of Opportunities: Hong Kong',
                  description: `By <b>Mr. Anthony Lau</b>, Director, Hong Kong Talent Engage`
                }
              ]
            },
            {
              time: '11:45 - 12:05pm',
              items: [
                {
                  title: 'Macao - the Land of Opportunities',
                  description: `By <b>Dr William Chao</b>, Secretary-General of the Macau SAR Government's Talent Development Committee`
                }
              ]
            },
            {
              time: `12:05 - 12:30pm`,
              items: [
                {
                  title: `Panel 1: 9+2 Cities: Synergies for Growth in the Greater Bay Area`,
                  description: `<ul>
                    <li>Mr Clement Woo, Under Secretary for Constitutional & Mainland Affairs</li>
                    <li>Mr Stephen Chan, Deputy Chief Executive, Bank of China (Hong Kong)</li>
                    <li>Mr C M Chan, Immediate Past Chairman, Law Society of Hong Kong</li>
                  </ul>
                `
                }
              ]
            },
            {
              time: `12:30 - 12:55pm`,
              items: [
                {
                  title: `Panel 2 : Hong Kong as Super Connector: Talent Opportunities in the Greater Bay Area`,
                  description: `<ul>
                   <li>Mr Louis Pong, CEO, Employers' Federation of Hong Kong</li>
                   <li>Mr David Ip, Founding Chairman, Hong Kong China Network Security Association</li>
                   <li>Mr Edward Au, President of Hong Kong Institute of Certified Public Accountants</li>
                   <li>Ir Peter Leung, Chairman, The Institution of Engineering and Technology Hong Kong</li>
                  </ul>`
                }
              ]
            },
            {
              time: `12:55 - 1:00pm`,
              items: [
                {
                  title: `Wrap up`
                }
              ]
            },
            // {
            //   time: `1:00 - 2:00pm`,
            //   items: [
            //     {
            //       title: `Lunch`
            //     }
            //   ]
            // },
            'CAREER FAIR<br/>Bayu Ballroom (6F) & Starhill 1 (4F)',
            {
              time: `2:00 - 6:00pm`,
              items: [
                {
                  title: `Career Fair`
                }
              ]
            }
          ]
        },
        {
          date: convertGMT8ToUTC('2025-04-13T00:00:00'),
          slots: [
            'CAREER FAIR<br/>Bayu Ballroom (6F) & Starhill 1 (4F)',
            {
              time: `10:00 - 4:00pm`,
              items: [
                {
                  title: `Career Fair`
                }
              ]
            }
          ]
        }
      ],
      locationDetails: {
        transports: [
          {
            title: 'By train',
            icon: defineAsyncComponent(
              () => import('~/components/common/icon/events/IconTrain.vue')
            ),
            instructions: [
              `The nearest MRT/LRT station is Bukit Bintang Station, just a short walk from JW Marriott.`,
              `If you're coming from KL Sentral, take the MRT Kajang Line or Monorail to Bukit Bintang for a direct connection.`
            ]
          },
          {
            title: 'By car',
            icon: defineAsyncComponent(() => import('~/components/common/icon/events/IconCar.vue')),
            instructions: [
              `From the city center, head towards Bukit Bintang via Jalan Sultan Ismail or Jalan Raja Chulan.`,
              `JW Marriott is located along Jalan Bukit Bintang, right next to Pavilion Kuala Lumpur.`,
              `Valet and self-parking are available at the hotel and nearby shopping malls.`
            ]
          },
          {
            title: 'By bus',
            icon: defineAsyncComponent(() => import('~/components/common/icon/events/IconBus.vue')),
            instructions: [
              `Several buses stop near JW Marriott, including those serving Pavilion KL (SBK18A) and Bukit Bintang (KL86, KL101, etc.).`,
              `Check RapidKL routes for the most convenient option based on your location.`
            ]
          }
        ]
      },
      //   sectors: {
      //     title: `Work in one of Hong Kong's <u>thriving industries</u>`,
      //     subtitle: `If you're looking to explore career opportunities in any of these industries - don't miss out on this event!`,
      //     style: `background: rgb(63,24,103); background: linear-gradient(180deg, rgba(63,24,103,0.45) 0%, rgba(63,24,103,1) 100%); opacity: 0.8;`,
      //     sectors: [
      //       {
      //         title: 'Information Technology',
      //         image: 'event-sector-code.png'
      //       },
      //       {
      //         title: 'Cybersecurity',
      //         image: 'event-sector-cybersecurity.png'
      //       },
      //       {
      //         title: 'Legal',
      //         image: 'event-sector-law.png'
      //       },
      //       {
      //         title: 'Business Services',
      //         image: 'event-sector-business.png'
      //       },
      //       {
      //         title: 'Engineering',
      //         image: 'event-sector-construction.png'
      //       },
      //       {
      //         title: 'Finance',
      //         image: 'event-sector-fin.png'
      //       },
      //       {
      //         title: 'Accounting',
      //         image: 'event-sector-account.png'
      //       }
      //     ]
      //   },
      attendee: {
        title: 'The <u>must attend</u> event for',
        subtitle: '',
        attendees: [
          {
            title: `Full Stack Engineer`,
            imageUrl: '/images/landing/events/attendee/attendee-software.svg'
          },
          {
            title: 'Cloud Solutions Engineer',
            imageUrl: '/images/landing/events/attendee/attendee-cloud.svg'
          },
          {
            title: 'AI/ML Engineer',
            imageUrl: '/images/landing/events/attendee/attendee-ml.svg'
          },
          {
            title: 'Penetration Tester',
            imageUrl: '/images/landing/events/attendee/attendee-pentest.svg'
          },
          {
            title: 'Security Operations Center (SOC) Analyst',
            imageUrl: '/images/landing/events/attendee/attendee-risk.svg'
          },
          {
            title: 'Cyber Risk & Compliance Analyst',
            imageUrl: '/images/landing/events/attendee/attendee-cybersecurity.svg'
          },
          {
            title: 'Intellectual Property (IP) Specialist',
            imageUrl: '/images/landing/events/attendee/attendee-content.svg'
          },
          {
            title: 'Contract & Commercial Law Specialist',
            imageUrl: '/images/landing/events/attendee/attendee-law.svg'
          },
          {
            title: 'ESG & Sustainability Legal Associate',
            imageUrl: '/images/landing/events/attendee/attendee-plant.svg'
          },
          {
            title: 'Business Development Executive',
            imageUrl: '/images/landing/events/attendee/attendee-business.svg'
          },
          {
            title: 'Digital Marketing Specialist',
            imageUrl: '/images/landing/events/attendee/attendee-hr.svg'
          },
          {
            title: 'Project Coordinator',
            imageUrl: '/images/landing/events/attendee/attendee-decision.svg'
          },
          {
            title: 'Electronics Engineer',
            imageUrl: '/images/landing/events/attendee/attendee-ai.svg'
          },
          {
            title: 'Civil Engineer (Infrastructure & Smart Cities)',
            imageUrl: '/images/landing/events/attendee/attendee-decision.svg'
          },
          {
            title: 'Robotics & Automation Engineer',
            imageUrl: '/images/landing/events/attendee/attendee-operation.svg'
          },
          {
            title: 'Junior Auditor',
            imageUrl: '/images/landing/events/attendee/attendee-qa.svg'
          },
          {
            title: 'Management Accountant',
            imageUrl: '/images/landing/events/attendee/attendee-data.svg'
          },
          {
            title: 'Forensic Accountant',
            imageUrl: '/images/landing/events/attendee/attendee-alarm.svg'
          },
          {
            title: 'Investment Analyst',
            imageUrl: '/images/landing/events/attendee/attendee-bank.svg'
          },
          {
            title: 'Corporate Finance Associate',
            imageUrl: '/images/landing/events/attendee/attendee-building.svg'
          },
          {
            title: 'Fintech Product Analyst',
            imageUrl: '/images/landing/events/attendee/attendee-wallet.svg'
          }
        ]
      }
    }
  },
  {
    id: 'hk-wo-24',
    title: 'Hong Kong: A World of Opportunities 2024',
    featured: false,
    type: 'Outreach Programme',
    mode: 'Physical',
    location: {
      country: 'Malaysia',
      short: 'The Ritz-Carlton, KL',
      full: `The Ritz-Carlton,<br/>168, Jln Imbi, Bukit Bintang,<br/>55100 Kuala Lumpur,<br/>Wilayah Persekutuan Kuala Lumpur<br/>Malaysia`,
      googleMapSrc:
        'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3983.801875006827!2d101.7153713!3d3.1469196!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31cc362dd2afffff%3A0x28ba98fb3bfbe45a!2sThe%20Ritz-Carlton%2C%20Kuala%20Lumpur!5e0!3m2!1sen!2smy!4v1726889905822!5m2!1sen!2smy'
    },
    date: [
      {
        // from: new Date('2024-10-07T17:00:00'),
        // to: new Date('2024-10-07T19:00:00')
        from: convertGMT8ToUTC('2024-10-07T17:00:00'),
        to: convertGMT8ToUTC('2024-10-07T19:00:00')
      }
    ],
    registrationPeriod: {
      from: convertGMT8ToUTC('2024-09-01T00:00:00'),
      to: convertGMT8ToUTC('2024-10-03T17:00:00')
    },
    banner: {
      light: false,
      mainImg: 'event-banner-hk-wo-24.png',
      contentImg: 'event-banner-content-hk-wo-24.svg',
      description: `Discover Hong Kong through our event, where we nurture top talent, offer networking, skill-building workshops, and career opportunities to foster professional growth in Hong Kong's dynamic and innovative industries.`
    },
    links: {
      hasMedia: false,
      links: [
        {
          url: '/signup?purpose=learn&invite_code=hk-wo-24&next=event-reg&event_id=hk-wo-24',
          buttonTitle: 'Join as talent',
          sectionTitle: `I am a jobseeker`,
          description:
            'Hong Kong is calling! Join our event and discover Hong Kong - a place where opportunity and growth meets.'
        },
        {
          url: '/signup?purpose=recruit&invite_code=hk-wo-24&next=event-reg&event_id=hk-wo-24',
          buttonTitle: 'Join as company representative',
          sectionTitle: 'I am a company representative',
          description: `Don't miss out on this opportunity to gain key insights into Hong Kong's market landscape and explore business opportunities.`
        }
      ]
    },
    sections: {
      overview: {
        description: `Interested to work in one of world's most dynamic cities?
            <br/><br/>
            We are thrilled to partner with Hong Kong Talent Engage to bring you the 1st Hong Kong Talent Outreach Programme in Malaysia! 
            <br/><br/>
            For job-seekers, this is your chance to connect with seasoned professionals, and to gain first-hand insights on adapting to life and work in Hong Kong.
            <br/><br/>
            For industry players, take this opportunity to understand the market landscape of Hong Kong and crucial practical considerations for business expansion.
            <br/><br/>
            Ready to expand your career in Hong Kong?`,
        image: '/images/database/events/event-overview-hk-wo-2024.png',
        showCountdown: false
      },
      employers: {
        title: `By Hong Kong's <u>industry leaders</u>`,
        subtitle: '',
        move: false,
        employers: ['hkawc', 'hkte', 'tl', 'tja', 'vialto']
      },
      offers: {
        offers: [
          {
            title: 'Get to know Hong Kong',
            imageUrl: '/images/database/events/event-offer-hk-wo-24-1.png',
            description: `Discover exciting career opportunities and learn how to make your move to Hong Kong.`
          },
          {
            title: 'Insights from success stories',
            imageUrl: '/images/database/events/event-offer-hk-wo-24-0.png',
            description: `Hear first-hand experiences from Malaysian professionals already thriving there.`
          },
          {
            title: 'Stay informed, stay ahead',
            imageUrl: '/images/database/events/event-offer-kl-cf-2024-2.png',
            description: `Get practical advice on taxes, relocation, and staying ahead of job market trends.`
          }
        ]
      },
      schedule: [
        {
          // date: new Date('2024-10-07'),
          date: convertGMT8ToUTC('2024-10-07T00:00:00'),
          slots: [
            {
              time: '5:00pm',
              items: [
                {
                  title: 'Registration with Coffee & Tea'
                }
              ]
            },
            {
              time: '5:30pm',
              items: [
                {
                  title: 'Opening Speech',
                  description: 'By Mr. Anthony Lau, Director of <b>Hong Kong Talent Engage</b>'
                }
              ]
            },
            {
              time: '5:45pm',
              items: [
                {
                  title: 'Welcome Note',
                  description:
                    'By Ms. Libera Cheng, Director-General, <b>Hong Kong Economic and Trade Office</b>'
                }
              ]
            },
            {
              time: '5:50pm',
              items: [
                {
                  title: 'Session 1',
                  description: 'By Mr. Terrance Lok, Chief Executive Officer of <b>TechJobAsia</b>'
                },
                {
                  title: 'Session 2 ',
                  description: 'By Ms. Lay Har Wee, Director of <b>Vialto Partners</b>'
                },
                {
                  title: 'Panel Discussion',
                  description:
                    'By Ms. Janice Chew, Partner, <b>JC Legal</b>, <br/> Ms. Rachel Tay, Assistant Director, <b>Tencent Foundation</b>, <br/>Mr. Sudesh Thevasenabathy, Head of Diversity, Enquiry and Inclusion, <b>Asia Manulife</b>'
                }
              ]
            },
            {
              time: '6:45pm',
              items: [
                {
                  title: 'Networking Session'
                }
              ]
            },
            {
              time: '7:15pm',
              items: [
                {
                  title: 'Event Ends'
                }
              ]
            }
          ]
        }
      ],
      locationDetails: {
        transports: [
          {
            title: 'By train',
            icon: defineAsyncComponent(
              () => import('~/components/common/icon/events/IconTrain.vue')
            ),
            instructions: [
              'Start at KL Sentral: Take the KTM Komuter, LRT, or Monorail to KL Sentral, the main transportation hub.',
              'Take the Monorail: From KL Sentral, board the KL Monorail towards Titiwangsa.',
              'Disembark at Bukit Bintang Station: Get off at Bukit Bintang Station.',
              `Walk to the Ritz-Carlton: It's about a 10-minute walk from the station. Head towards Jalan Imbi and follow the signs to the hotel.`
            ]
          },
          {
            title: 'By car',
            icon: defineAsyncComponent(() => import('~/components/common/icon/events/IconCar.vue')),
            instructions: [
              `From KL Sentral: Head southeast on Jalan Tun Sambanthan, then turn onto Jalan Pudu.`,
              `Merge onto Jalan Imbi: Follow signs to Jalan Imbi.`,
              `Arrive at the Ritz-Carlton: The hotel will be on your left. Look for the entrance for parking. Make sure to check traffic conditions, as they can vary throughout the day!`
            ]
          }
        ]
      },
      sectors: {
        title: `Work in one of Hong Kong's <u>thriving industries</u>`,
        subtitle: `If you're looking to explore career opportunities in any of these industries - don't miss out on this event!`,
        sectors: [
          {
            title: 'Business',
            image: 'event-sector-business.png'
          },
          {
            title: 'Creative Industries, Arts & Culture, Performing Arts',
            image: 'event-sector-art.png'
          },
          {
            title: 'Development & Construction',
            image: 'event-sector-construction.png'
          },
          {
            title: 'Environmental Technology Services',
            image: 'event-sector-green.png'
          },
          {
            title: 'Financial Services',
            image: 'event-sector-fin.png'
          },
          {
            title: 'Healthcare Services',
            image: 'event-sector-health.png'
          },
          {
            title: 'Innovation and Technology',
            image: 'event-sector-robot.png'
          },
          {
            title: 'Legal and Dispute Resolution Services',
            image: 'event-sector-law.png'
          },
          {
            title: 'Maritime Services',
            image: 'event-sector-ship.png'
          }
        ]
      }
    }
  },
  {
    id: 'kl-cf-2024',
    title: 'Tech+Digital Career Fair 2024',
    featured: false,
    type: 'Career Fair',
    mode: 'Physical',
    location: {
      country: 'Malaysia',
      short: 'Royale Chulan, KL',
      full: 'Royale Chulan, <br/>No. 5, Jalan Conlay<br/>50450 W. P. Kuala Lumpur,<br/>Malaysia',
      googleMapSrc:
        'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3983.792929537863!2d101.71448127497115!3d3.1492587968261447!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31cc362d6c1630ed%3A0x7f274f287562f37!2sRoyale%20Chulan%20Kuala%20Lumpur!5e0!3m2!1sen!2smy!4v1717600387409!5m2!1sen!2smy'
    },
    date: [
      {
        // from: new Date('2024-08-17T10:00:00'),
        // to: new Date('2024-08-17T18:00:00')
        from: convertGMT8ToUTC('2024-08-17T10:00:00'),
        to: convertGMT8ToUTC('2024-08-17T18:00:00')
      },
      {
        // from: new Date('2024-08-18T10:00:00'),
        // to: new Date('2024-08-18T18:00:00')
        from: convertGMT8ToUTC('2024-08-18T10:00:00'),
        to: convertGMT8ToUTC('2024-08-18T18:00:00')
      }
    ],
    banner: {
      light: false,
      mainImg: 'event-banner-dark.png',
      contentImg: 'event-banner-content-kl-cf-2024.svg',
      description:
        'The demand for skilled and experienced technology professionals has never been higher. Take the first step into boosting your tech career and get connected with leading companies!'
    },
    links: {
      hasMedia: false,
      links: [
        {
          url: '/signup?purpose=learn&invite_code=kl-cf-2024&next=event-reg&event_id=kl-cf-2024',
          buttonTitle: 'Join as Jobseeker',
          sectionTitle: `I am a Talent`,
          description: `Secure your spot at our Career Fair and private networking events. Experience the ultimate digital career fair and instant job opportunities all day long!`
        },
        {
          url: '/signup?purpose=recruit&invite_code=kl-cf-2024&next=event-reg&event_id=kl-cf-2024',
          buttonTitle: 'Join as Exhibitor',
          sectionTitle: `I am an Employer`,
          description: `Cultivate your talent pool by securing a spot as an exhibitor. Hire exceptional digital talents and secure your spot today to unlock access to top-tier candidates and cultivate a dynamic talent pipeline that propels your company forward.`
        }
      ]
    },
    sections: {
      overview: {
        description: `Welcome to our Annual Career Fair, the premier event where ambition meets opportunity! Join us for a dynamic day of networking, learning, and career advancement. 
        <br/><br/>
        Whether you're a seasoned professional or just starting your career journey, this event is designed to connect you with top employers and industry leaders eager to discover new talent. 
        <br/><br/>
        Engage in insightful workshops, gain valuable insights from expert panels, and explore a wide range of job opportunities across various fields. This is your chance to unlock your potential, build meaningful connections, and take the next step toward a successful and fulfilling career.`,
        image: '/images/database/events/event-overview-kl-cf-2024.png',
        showCountdown: false
      },
      employers: {
        title: `Meet your future <u>employers</u>`,
        move: true,
        subtitle:
          'Discover potential employers and enhance your professional network with our panel of top companies',
        employers: [
          'grab',
          'shopback',
          'moovby',
          'tng2',
          'mpay',
          'setel',
          'azmi',
          'ecv',
          'capbay',
          'antint',
          'seek',
          'influasia',
          'umobile',
          'pwc',
          'compasia',
          'aamove2',
          'deloitte',
          'wob',
          'kpmg',
          'trapo',
          'basf',
          'softspace',
          'nd',
          'experian',
          'mbsb',
          'lm',
          'agmo',
          'nebu'
        ]
      },
      offers: {
        offers: [
          {
            title: 'Career Lounge',
            imageUrl: '/images/database/events/event-offer-kl-cf-2024-0.png',
            description:
              'Discover your career path at our one stop booth. Connect with experienced advisors for:',
            points: ['Resume refinement', 'Elevator pitch practice', 'Interview TIPS']
          },
          {
            title: 'Professional Headshot',
            imageUrl: '/images/database/events/event-offer-kl-cf-2024-1.png',
            description:
              'Elevate your online presence while connecting with potential employers effortlessly.',
            points: [
              'Professional headshot on-site',
              'Experienced headshot photographer',
              'Free of charge'
            ]
          },
          {
            title: 'Panel Discussion',
            imageUrl: '/images/database/events/event-offer-kl-cf-2024-2.png',
            description:
              'Broaden your horizons and explore new opportunities at our industry talks.',
            points: [
              'Gain valuable industry insights',
              'Ask questions and interact with industry experts'
            ]
          },
          {
            title: 'Lucky Draw + Exclusive Prizes',
            imageUrl: '/images/database/events/event-offer-kl-cf-2024-3.png',
            description: 'Make your career fair experience unforgettable!',
            points: [
              'A chance to win a range of valuable prizes',
              'From branded tote bags to a Nintendo Switch'
            ]
          }
        ]
      },
      schedule: [
        {
          // date: new Date('2024-08-17'),
          date: convertGMT8ToUTC('2024-08-17T00:00:00'),
          slots: [
            {
              time: '10:00am',
              items: [
                {
                  title: 'Exhibitor booths open',
                  description:
                    'From 10am to 6pm, our exhibitor booths welcome you to network, seek insights, and secure potential interviews with leading companies.'
                }
              ]
            },
            {
              time: '10:30am',
              items: [
                {
                  title: 'Headshot photobooth opens',
                  description: `Unleash the power of a captivating headshot at our FREE professional photo booth. Whether it's a formal portrait or a casual snapshot, let your personality shine and leave a memorable first impression!`
                }
              ]
            },
            {
              time: '11am - 4pm',
              items: [
                {
                  title: 'Career lounge',
                  description:
                    'Our Career Lounge is your go-to hub for resume enhancements, mock interviews, and expert career guidance. Swing by anytime to fine-tune your job search strategy and gain valuable insights from our advisors!'
                }
                // { title: 'Industry talk 1' }
              ]
            },
            {
              time: '11am - 5pm',
              items: [
                {
                  title: 'Industry talks',
                  description:
                    'Gain valuable insights with our industry experts, happening every hour.'
                }
              ]
            },
            // {
            //     time: '12:00pm',
            //     items: [{ title: 'Industry talk ' }]
            //   },
            // {
            //   time: '1:00pm',
            //   items: [{ title: 'Industry talk 3' }]
            // },
            // {
            //   time: '2:00pm',
            //   items: [{ title: 'Industry talk 4' }]
            // },
            // {
            //   time: '3:00pm',
            //   items: [{ title: 'Industry talk 5' }]
            // },
            // {
            //   time: '4:00pm',
            //   items: [{ title: 'Industry talk 6' }]
            // },
            {
              time: '5:00pm',
              items: [
                {
                  title: 'Lucky draw',

                  description: `Join us for the thrilling Lucky Draw Prize Ceremony, where luck meets opportunity. Be present to discover if you're one of the fortunate winners of our exciting prizes.`
                }
              ]
            }
          ]
        },
        {
          // date: new Date('2024-08-18'),
          date: convertGMT8ToUTC('2024-08-18T00:00:00'),
          slots: [
            {
              time: '10:00am',
              items: [
                {
                  title: 'Exhibitor booths open',
                  description:
                    'From 10am to 6pm, our exhibitor booths welcome you to network, seek insights, and secure potential interviews with leading companies.'
                }
              ]
            },
            {
              time: '10:30am',
              items: [
                {
                  title: 'Headshot photobooth opens',
                  description: `Unleash the power of a captivating headshot at our FREE professional photo booth. Whether it's a formal portrait or a casual snapshot, let your personality shine and leave a memorable first impression!`
                }
              ]
            },
            {
              time: '11am - 4pm',
              items: [
                {
                  title: 'Career lounge',
                  description:
                    'Our Career Lounge is your go-to hub for resume enahncements, mock interviews, and expert career guidance. Swing by anytime to fine-tune your job search strategy and gain valuabel insights from our advisors!'
                }
                // { title: 'Industry talk 1' }
              ]
            },
            {
              time: '11am - 5pm',
              items: [
                {
                  title: 'Industry talks',
                  description:
                    'Gain valuable insights with our industry experts, happening every hour.'
                }
              ]
            },
            // {
            //     time: '12:00pm',
            //     items: [{ title: 'Industry talk ' }]
            //   },
            // {
            //   time: '1:00pm',
            //   items: [{ title: 'Industry talk 3' }]
            // },
            // {
            //   time: '2:00pm',
            //   items: [{ title: 'Industry talk 4' }]
            // },
            // {
            //   time: '3:00pm',
            //   items: [{ title: 'Industry talk 5' }]
            // },
            // {
            //   time: '4:00pm',
            //   items: [{ title: 'Industry talk 6' }]
            // },
            {
              time: '5:00pm',
              items: [
                {
                  title: 'Lucky draw',

                  description: `Join us for the thrilling Lucky Draw Prize Ceremony, where luck meets opportunity. Be present to discover if you're one of the fortunate winners of our exciting prizes.`
                }
              ]
            }
          ]
        }
      ],
      locationDetails: {
        transports: [
          {
            title: 'By car',
            icon: defineAsyncComponent(() => import('~/components/common/icon/events/IconCar.vue')),
            instructions: [
              "If you're coming from the city center, take Jalan Bukit Bintang towards Jalan Raja Chulan.",
              'Continue on Jalan Raja Chulan until you reach Jalan Conlay.',
              "Turn left onto Jalan Conlay, and you'll find the hotel on your left-hand side.",
              'Valet parking and self-parking options are available at the hotel.'
            ]
          },
          {
            title: 'By train',
            icon: defineAsyncComponent(
              () => import('~/components/common/icon/events/IconTrain.vue')
            ),
            instructions: [
              'The nearest MRT station is the Conlay station. From there it is a 3 minute walk to the hotel.',
              'Alternatively, you can stop at Bukit Bintang MRT station or take the monorail to the Raja Chulan station, both of which are within a walking distance of the hotel.',
              'Both the MRT and monorail provide convenient connections to various parts of the city.'
            ]
          },
          {
            title: 'By bus',
            icon: defineAsyncComponent(() => import('~/components/common/icon/events/IconBus.vue')),
            instructions: [
              'The nearest bus stops include Dorsett Residence KL (KL268), KL Convention Centre (Barat) (KL88), Wisma Cosway (KL83) and Bukit Bintang (KL86)',
              'Depending on your starting point, you can catch a bus to one of these stops and take a short walk to reach the hotel.'
            ]
          }
        ]
      }
    }
  },
  {
    id: 'c-cf-2024',
    title: 'Cyberport Career Fair 2024',
    featured: false,
    type: 'Career Fair',
    mode: 'Physical',
    location: {
      country: 'Hong Kong',
      short: 'Cyberport, Hong Kong',
      full: 'Cyberport, Hong Kong',
      googleMapSrc:
        'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3692.422020343071!2d114.12765297573736!3d22.261998079714147!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3403ffee35af3037%3A0x289fb0f444906fa2!2sCyberport!5e0!3m2!1sen!2smy!4v1717627436129!5m2!1sen!2smy'
    },
    date: [
      {
        // from: new Date('2024-04-15T09:00:00'),
        // to: new Date('2024-04-15T17:00:00')
        from: convertGMT8ToUTC('2024-04-15T09:00:00'),
        to: convertGMT8ToUTC('2024-04-15T17:00:00')
      },
      {
        // from: new Date('2024-04-16T09:00:00'),
        // to: new Date('2024-04-16T17:00:00')
        from: convertGMT8ToUTC('2024-04-16T09:00:00'),
        to: convertGMT8ToUTC('2024-04-16T17:00:00')
      }
    ],
    banner: {
      light: true,
      mainImg: 'event-banner-light.png',
      contentImg: 'event-banner-content-c-cf-2024.svg',
      description:
        'Join us at the Career Fair, where top employers and industry leaders connect with aspiring professionals for unparalleled networking and career opportunities.'
    },
    links: {
      hasMedia: false,
      links: [
        {
          url: '',
          buttonTitle: 'Visit the fair now',
          sectionTitle: 'Spark your digital career',
          description:
            'Connect with top employers and explore exciting career opportunities at our upcoming Career Fair.'
        }
      ]
    },

    sections: {
      overview: {
        description: `TechJobAsia partnered with Cyberport, bringing a list of career development resources to the Cyberport Career Fair 2024 participants.
        <br/>
        <br/>
        With our free services, you would be able to get free personalised CV consultations, recommended job openings and more.
        <br/>
        <br/>
        Explore now and get a better tech career in 2024!`,
        showCountdown: false
      },
      offers: {
        offers: [
          {
            imageUrl: '/images/database/events/event-offer-c-cf-2024-0.png',
            title: 'CV Clinic',
            description:
              'CV is the first step of job hunting. Leverage online AI CV scanning or physical CV consultation, to help you improve your CV and personal profile.'
          },
          {
            imageUrl: '/images/database/events/event-offer-c-cf-2024-1.png',
            actionLink: '',
            buttonTitle: 'Start scanning',
            title: 'Online CV Review',
            description:
              'Our AI algorithm automatically evaluate your CV against thousands of job openings, and give advice on how you can improve your CV.'
          },
          {
            imageUrl: '/images/database/events/event-offer-c-cf-2024-2.png',
            actionLink: '',
            buttonTitle: 'Book a session',
            title: 'Physical Consultation',
            description:
              'Our professional consultants would provide 1 to 1 consultation with you in Cyberport during the Career Fair 2024. Ask questions and get career advices there.'
          }
        ]
      }
    }
  },
  {
    id: 'webinar-0',
    title: 'Algorithm Interview Webinar',
    featured: false,
    type: 'Webinar',
    mode: 'Online',
    date: [
      {
        // from: new Date('2024-02-10T15:00:00'),
        // to: new Date('2024-02-10T16:00:00')
        from: convertGMT8ToUTC('2024-02-10T15:00:00'),
        to: convertGMT8ToUTC('2024-02-10T16:00:00')
      }
    ],
    banner: {
      light: true,
      mainImg: 'event-banner-light.png',
      contentImg: 'event-banner-content-webinar-0.svg',
      description:
        'Join our Algorithm Workshop Webinar to master fundamental and advanced algorithmic techniques from industry experts and elevate your coding skills.'
    },
    links: {
      hasMedia: false,
      links: [
        {
          url: '',
          buttonTitle: 'Go to Webinar',
          sectionTitle: 'Join us now',
          description: `Enhance your coding skills with advanced algorithm techniques in our expert-led webinar.`
        }
      ]
    },

    sections: {
      overview: {
        description: `Join us for an in-depth Algorithm Workshop Webinar, where innovation meets expertise! This interactive session is designed for both budding programmers and seasoned developers eager to enhance their algorithmic skills. 
        <br/><br/>
        Led by industry experts, the webinar will cover fundamental concepts, advanced techniques, and practical applications of algorithms across various domains. Dive into problem-solving strategies, learn to optimize your code, and discover best practices to tackle complex computational challenges. Whether you're aiming to ace technical interviews or streamline your projects, this workshop provides the tools and insights to elevate your programming prowess.`,
        showCountdown: false
      },
      offers: {
        offers: [
          {
            title: 'Fundamental Concepts',
            imageUrl: '/images/database/events/event-offer-webinar-0-0.png',
            description: `Understand essential algorithmic principles like sorting, searching, and data structures, building a strong foundational knowledge.`
          },
          {
            title: 'Advanced Techniques',
            imageUrl: '/images/database/events/event-offer-webinar-0-1.png',
            description: `Explore dynamic programming, graph algorithms, and optimization methods to tackle complex coding challenges.`
          },
          {
            title: 'Practical Applications',
            imageUrl: '/images/database/events/event-offer-webinar-0-2.png',
            description: `Apply your knowledge through hands-on exercises and real-world examples, seeing the impact of optimized algorithms in action.`
          }
        ]
      },
      speakers: [
        {
          name: 'Darren Chiu',
          job: 'Technical Consultant',
          imageUrl: '/images/database/teams/team-darren.png',
          logoUrl: '/images/database/teams/team-logo-google.png'
        },
        {
          name: 'Anthony Chiu',
          job: 'Software Engineer',
          imageUrl: '/images/database/teams/team-anthony.png',
          logoUrl: '/images/database/teams/team-logo-clover.png'
        }
      ]
    }
  },
  {
    id: 'webinar-1',
    title: 'Career Talk Webinar',
    featured: false,
    type: 'Webinar',
    mode: 'Online',
    date: [
      {
        // from: new Date('2024-01-08T16:00:00'),
        // to: new Date('2024-01-08T17:00:00')
        from: convertGMT8ToUTC('2024-01-08T16:00:00'),
        to: convertGMT8ToUTC('2024-01-08T17:00:00')
      }
    ],
    banner: {
      light: false,
      mainImg: 'event-banner-dark.png',
      contentImg: 'event-banner-content-webinar-1.svg',
      description:
        'Get inspired and informed with career insights from leading professionals at our Career Talk Webinar.'
    },
    links: {
      hasMedia: false,
      links: [
        {
          url: '',
          buttonTitle: 'Go to Webinar',
          sectionTitle: 'Join us now',
          description:
            'Join us for an insightful Career Talk Webinar, where seasoned professionals share actionable tips and strategies to advance your career and achieve your professional aspirations.'
        }
      ]
    },

    sections: {
      overview: {
        description: `Step into our Career Talk Webinar, a unique opportunity to gain exclusive insights from accomplished industry veterans and trailblazing professionals. 
        <br/><br/>
        This dynamic session is tailored for individuals at all career stages, from recent graduates to seasoned professionals looking to pivot or advance. Our speakers will share their personal success stories, innovative strategies for job searching, and practical advice for professional development. Engage in interactive discussions, get your questions answered, and uncover the secrets to navigating today's competitive job market.`,
        showCountdown: false
      },
      offers: {
        offers: [
          {
            title: 'Expert Career Advice',
            imageUrl: '/images/database/events/event-offer-webinar-1-0.png',
            description: `Receive personalized tips and strategies from industry leaders to enhance your job search and career development.`
          },
          {
            title: 'Networking Opportunities',
            imageUrl: '/images/database/events/event-offer-webinar-1-1.png',
            description: `Connect with professionals and peers, expanding your network to open doors for future career opportunities.`
          }
        ]
      },
      speakers: [
        {
          name: 'Terrance Lok',
          imageUrl: '/images/database/teams/team-terrance.png',
          logoUrl: '/images/database/teams/team-logo-siemens.png',
          job: 'Technical Engineer'
        }
      ]
    }
  }
]
